'use client';

import type {
  InteractionType,
} from '~/model/InteractionType';
import type { ReactionType } from '~/model/ReactionType';
import { VpButton, VpIcon } from '@vtmn-play/react/headless';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { fetchItem } from '@/app/actions/server';
import { Loader } from '@/app/components/Loader';
import { useAuthenticatedContext } from '@/app/components/Provider';
import { getIdFromUri } from '@/app/utils/entities/uri';
import { api } from '~/const/api';

export const AnnouncementFooter = ({
  interaction,
}: {
  interaction: InteractionType;
}) => {
  const t = useTranslations('ANNOUNCEMENT');
  const { isAuthenticated } = useAuthenticatedContext();

  const [loading, setLoading] = useState(false);
  const [userReactionId, setUserReactionId] = useState<number>();
  const [reactions, setReactions] = useState(
    interaction.reactions_repartition,
  );

  const isActive = !!userReactionId;

  const updateLikeCount = (increase: boolean) => {
    const { like = 0 } = reactions || {};
    setReactions(previous => ({
      ...previous,
      like: increase ? like + 1 : like - 1,
    }));
  };

  const handleAnnouncementLike = async () => {
    setLoading(true);

    if (userReactionId) {
      await fetchItem<ReactionType>({
        pathname: `reactions/${userReactionId}`,
        method: 'DELETE',
        revalidate: 0,
      });

      updateLikeCount(false);
      setUserReactionId(null);
    } else {
      const response = await fetchItem<ReactionType>({
        pathname: 'reactions',
        method: 'POST',
        revalidate: 0,
        body: {
          interaction: `/api/${api.INTERACTIONS}/${interaction.id}`,
          value: 'like',
        },
      });

      updateLikeCount(true);
      setUserReactionId(response.id);
    }

    setLoading(false);
  };

  useEffect(() => {
    setUserReactionId(
      getIdFromUri(interaction?.current_user_contribution?.reaction?.['@id']),
    );
  }, [interaction?.current_user_contribution?.reaction]);

  return (
    <div className="announcement__header">
      {isAuthenticated
        ? (
            <span
              className={classNames('announcement__like', {
                'announcement__like--loading': loading,
              })}
            >
              <VpButton
                data-cy="like"
                data-testid="announcement-like"
                variant={isActive ? 'primary' : 'secondary'}
                startSlot={<VpIcon name={isActive ? 'heart-filled' : 'heart'} />}
                {...(reactions?.like > 0 && {
                  endSlot: (
                    <span data-cy="like-counter">
                      {reactions.like}
                    </span>
                  ),
                })}
                onClick={handleAnnouncementLike}
                disabled={loading}
              >
                {t('LIKE_CONTENT')}
              </VpButton>
              {loading && (
                <span className="announcement__like-loading">
                  <Loader loading size="small" />
                </span>
              )}
            </span>
          )
        : (
            <span className="vp-body-l announcement__like--disconnected">
              <VpIcon name="heart" />
              {' '}
              {reactions?.like || 0}
            </span>
          )}
    </div>
  );
};
